import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NewHeader from "../../../Home/NewHeader";
import Breadcrumb from "../../ProfileCommon/Breadcrumb";
import { FormSelect } from "../../../../components/common/Select/FormSelect";
import images from "../../../../images";
import UploadReceiptConntact from "../../ProfileCommon/UploadReceiptConntact";
import { usePostContactMutation } from "../../../../services/apiSlice";
import LoadingSpinner from "../../../../components/common/Loading/LoadingSpinner";
import LoadingCommon from "../../../../components/common/LoadingStatus/LoadingCommon";

const ContactUS = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // API hook
  const [postContact, { isLoading }] = usePostContactMutation();

  // Subject options
  const subjectOptions = [
    { label: "General Inquiry", value: "general" },
    { label: "Technical Support", value: "technical" },
    { label: "Feedback", value: "feedback" },
    { label: "Other", value: "other" },
  ];

  // State management
  const [selectedSubject, setSelectedSubject] = useState(subjectOptions[0]);
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoadingCommon, setIsLoadingCommon] = useState(true);

  const [errors, setErrors] = useState({
    subject: "",
    email: "",
    description: "",
    files: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingCommon(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleSubjectChange = (option) => {
    setSelectedSubject(option);
    if (errors.subject) {
      setErrors((prev) => ({ ...prev, subject: "" }));
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    if (errors.description) {
      setErrors((prev) => ({ ...prev, description: "" }));
    }
  };

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
    if (errors.files) {
      setErrors((prev) => ({ ...prev, files: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      subject: "",
      email: "",
      description: "",
      files: "",
    };

    if (!selectedSubject || !selectedSubject.value) {
      newErrors.subject = "Please select a subject";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!description.trim()) {
      newErrors.description = "Please enter a description";
    }

    // if (!uploadedFiles || uploadedFiles.length === 0) {
    //   newErrors.files = "Please upload at least one file";
    // }

    setErrors(newErrors);

    return (
      !newErrors.subject &&
      !newErrors.email &&
      !newErrors.description &&
      !newErrors.files
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("subject", selectedSubject.label);
      formData.append("email", email.trim());
      formData.append("description", description.trim());

      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          formData.append("files", file);
        });
      }

      await postContact({ formData }).unwrap();

      setSelectedSubject(subjectOptions[0]);
      setEmail("");
      setDescription("");
      setUploadedFiles([]);
      setErrors({ subject: "", email: "", description: "", files: "" });

      toast.success("Contact message sent successfully!");

      setTimeout(() => {
        navigate("/profile/support");
      }, 1500);
    } catch (error) {
      console.error("❌ Contact submission failed:", error);

      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        "Failed to send contact message";
      toast.error(errorMessage);
    }
  };

  const isFormValid =
    selectedSubject && email.trim().length > 0 && description.trim().length > 0;
  // &&
  // uploadedFiles.length > 0;

  return (
    <div className="flex overflow-hidden flex-col mx-auto w-full bg-white max-w-[480px]">
      <LoadingCommon statusLoading={isLoadingCommon} />
      <div className="flex flex-col w-full min-h-[912px]">
        <div className=" fixed top-0 left-0 w-full z-50">
          <NewHeader />
        </div>
        <div className="h-[30px]"></div>
        <div className="flex flex-col flex-1 gap-3 py-3.5 px-[30px] mt-4">
          <Breadcrumb
            className={"text-[#333333]"}
            breadCrumbTiltle={t("support.contactUs")}
            // onClickBack={() => navigate("/profile/support")}
            onClickBack={() => navigate(-1)}
          />

          <div className="space-y-4">
            {/* Subject Selection */}
            <div>
              <FormSelect
                icon={images.TagIcon}
                value={selectedSubject}
                onChange={handleSubjectChange}
                options={subjectOptions}
                name="subject"
                disabled={isLoading}
                className={errors.subject ? "border-red-500" : ""}
              />
              {/* Error message cho subject */}
              {errors.subject && (
                <div className="text-red-500 text-sm mt-1 px-2">
                  {errors.subject}
                </div>
              )}
            </div>

            {/* Email Input */}
            <div>
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={t("contactUs.enterEmail")}
                className={`border w-full mt-4 p-2 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-blue-500"
                }`}
                disabled={isLoading}
                maxLength={255}
              />
              {errors.email && (
                <div className="text-red-500 text-sm mt-1 px-2">
                  {errors.email}
                </div>
              )}
            </div>

            {/* Description Textarea */}
            <div>
              <textarea
                value={description}
                onChange={handleDescriptionChange}
                placeholder={t("contactUs.pleaseTypeHere")}
                className={`min-h-40 border w-full mt-4 p-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent ${
                  errors.description
                    ? "border-red-500 focus:ring-red-500"
                    : "focus:ring-blue-500"
                }`}
                disabled={isLoading}
                maxLength={1000}
              />

              {errors.description && (
                <div className="text-red-500 text-sm mt-1 px-2">
                  {errors.description}
                </div>
              )}
            </div>

            <div className="mt-8">
              <UploadReceiptConntact
                label={t("contactUs.uploadAttachment")}
                onFileUpload={handleFileUpload}
                maxFiles={1}
                initialFiles={uploadedFiles}
                handleNextStep={() => {}}
                setFileUploaded={() => {}}
                isUploading={isLoading}
                hideSubmitButton={true}
                hasError={!!errors.files}
              />

              {errors.files && (
                <div className="text-red-500 text-sm mt-1 px-2">
                  {errors.files}
                </div>
              )}
            </div>

            <div className="mt-8">
              {isLoading ? (
                <div className="flex justify-center items-center bg-[#ECEEFF] px-[18px] py-3 rounded-md h-[50px]">
                  <LoadingSpinner size="xs" />
                  {/* <FadeLoader color="#3534ff" loading={true} size={15} /> */}
                </div>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isLoading}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    isFormValid && !isLoading
                      ? "bg-[#253FF6] text-white hover:bg-blue-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {/* {isLoading ? t("base.submitting") : t("base.submit")} */}
                  {t("base.submit")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUS;
